import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getFirestore } from 'firebase/firestore';
import {
  loadUserComparison,
  readLocalComparisonItems,
  saveComparisonPropertyIds,
  dedupeComparisonProperties,
} from '../utils/comparisonStorage';

const ComparisonContext = createContext();

export function ComparisonProvider({ children }) {
  const [comparisonProperties, setComparisonProperties] = useState(() =>
    dedupeComparisonProperties(readLocalComparisonItems())
  );
  // Começa em loading se não há cache local — evita flash da tela vazia
  // enquanto auth/remoto ainda não hidrataram a comparação.
  const [loading, setLoading] = useState(
    () => readLocalComparisonItems().length === 0
  );
  const { user, loading: authLoading, localDataMigrationToken } = useAuth();
  const location = useLocation();
  const deferRemoteHydration = location.pathname === '/login';
  const MAX_COMPARISON_ITEMS = 4;

  useEffect(() => {
    // Enquanto o auth resolve, não marcar como vazio — pode haver dados remotos.
    if (authLoading) return;

    if (!user) {
      const savedComparison = readLocalComparisonItems();
      setComparisonProperties(dedupeComparisonProperties(savedComparison));
      setLoading(false);
      return;
    }

    // Na tela de login não hidrata comparação remota (evita N leituras de properties).
    if (deferRemoteHydration) {
      setComparisonProperties(dedupeComparisonProperties(readLocalComparisonItems()));
      setLoading(false);
      return;
    }

    // Mostra local imediatamente; só bloqueia UI se não houver nada ainda
    const localItems = dedupeComparisonProperties(readLocalComparisonItems());
    if (localItems.length > 0) {
      setComparisonProperties(localItems);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const db = getFirestore();
    let cancelled = false;

    (async () => {
      try {
        const properties = await loadUserComparison(db, user.uid);
        if (!cancelled) setComparisonProperties(dedupeComparisonProperties(properties));
      } catch (error) {
        if (cancelled) return;
        console.error('Erro ao carregar comparação:', error);
        const savedComparison = readLocalComparisonItems();
        setComparisonProperties(dedupeComparisonProperties(savedComparison));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, localDataMigrationToken, deferRemoteHydration]);

  useEffect(() => {
    if (user) return;

    const handleStorageChange = (e) => {
      if (e.key === 'comparisonProperties') {
        setComparisonProperties(dedupeComparisonProperties(readLocalComparisonItems()));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  const saveComparisonProperties = async (properties) => {
    try {
      if (user) {
        const db = getFirestore();
        await saveComparisonPropertyIds(db, user.uid, properties);
      } else {
        localStorage.setItem('comparisonProperties', JSON.stringify(properties));
      }
    } catch (error) {
      console.error('Erro ao salvar imóveis em comparação:', error);
      localStorage.setItem('comparisonProperties', JSON.stringify(properties));
    }
  };

  const addToComparison = async (property) => {
    if (comparisonProperties.some((p) => p.id === property.id)) {
      return { success: false, message: 'Este imóvel já está na comparação' };
    }

    if (comparisonProperties.length >= MAX_COMPARISON_ITEMS) {
      return {
        success: false,
        message: `Você pode comparar no máximo ${MAX_COMPARISON_ITEMS} imóveis. Remova um imóvel antes de adicionar outro.`,
      };
    }

    const newComparison = [...comparisonProperties, property];
    setComparisonProperties(newComparison);
    await saveComparisonProperties(newComparison);

    return { success: true, message: 'Imóvel adicionado à comparação' };
  };

  const removeFromComparison = async (propertyId) => {
    const newComparison = comparisonProperties.filter((p) => p.id !== propertyId);
    setComparisonProperties(newComparison);
    await saveComparisonProperties(newComparison);
  };

  const clearComparison = async () => {
    setComparisonProperties([]);
    await saveComparisonProperties([]);
    return { success: true, message: 'Lista de comparação limpa com sucesso!' };
  };

  const isInComparison = (propertyId) => comparisonProperties.some((p) => p.id === propertyId);

  const canAddMore = () => comparisonProperties.length < MAX_COMPARISON_ITEMS;

  const getComparisonCount = () => comparisonProperties.length;

  const value = {
    comparisonProperties,
    loading,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    canAddMore,
    getComparisonCount,
    MAX_COMPARISON_ITEMS,
  };

  return <ComparisonContext.Provider value={value}>{children}</ComparisonContext.Provider>;
}

export function useComparison() {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison deve ser usado dentro de um ComparisonProvider');
  }
  return context;
}
